/* eslint-disable @typescript-eslint/no-deprecated */

import React from 'react'
import styled, { useTheme } from 'styled-components'
import { Modal as MatModal } from '@mui/material'
import { CancelOutlined } from '@mui/icons-material'
import { SubHeading } from '../Typography'
import { Breakpoint } from '../../styles'

type Widths = 'small' | 'medium' | 'full-width'

interface ModalProps {
  open: boolean
  background?: string
  onClose?: (reason: 'backdropClick' | 'escapeKeyDown') => void
  title?: string
  body?: React.ReactElement | string
  width?: Widths
  backdropColor?: string
  closeIcon?: boolean
  heroImageSrc?: string
}

export const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  title,
  body,
  onClose,
  open,
  background,
  width,
  backdropColor,
  children,
  closeIcon,
  heroImageSrc,
}) => {
  const { colors } = useTheme()
  const backgroundIn = background ?? colors.white

  const handleClose = (reason: 'backdropClick' | 'escapeKeyDown') => {
    if (onClose) onClose(reason)
  }

  return (
    <CenteredModal
      aria-labelledby="modal-title"
      aria-describedby="modal-body"
      open={open}
      onClose={(event, reason) => handleClose(reason)}
      BackdropProps={backdropColor ? { style: { backgroundColor: backdropColor } } : {}}
    >
      <Container background={backgroundIn} width={width ?? 'small'}>
        {heroImageSrc && <HeroImage imageSrc={heroImageSrc} />}
        {title && (
          <ModalHeader>
            {title && <ModalTitle>{title}</ModalTitle>}
            {closeIcon && <CloseIcon onClose={onClose} />}
          </ModalHeader>
        )}
        <div id="modal-body">{body ?? children}</div>
      </Container>
    </CenteredModal>
  )
}

const ModalTitle: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <SubHeading mt={0} id="modal-title">
    {children}
  </SubHeading>
)

const CloseIcon: React.FC<{ onClose?: (reason: 'backdropClick' | 'escapeKeyDown') => void }> = ({ onClose }) => {
  const { colors } = useTheme()

  const handleClose = (reason: 'backdropClick' | 'escapeKeyDown') => {
    if (onClose) onClose(reason)
  }

  return (
    <Icon onClick={() => handleClose('backdropClick')}>
      <CancelOutlined style={{ fontSize: '2.4rem', color: colors.primary.medium }} />
    </Icon>
  )
}

const modalPaddingInRem = 4
const modalMobilePaddingInRem = 2

interface ContainerProps {
  background: string
  width: Widths
}

const CenteredModal = styled(MatModal)`
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(3px);
`

const HeroImage = styled.div<{ imageSrc: string }>`
  height: 25rem;
  background-position: center;
  background-image: ${({ imageSrc }) => `url(${imageSrc})`};
  background-size: cover;
  margin: ${-modalPaddingInRem}rem ${-modalPaddingInRem}rem ${modalPaddingInRem}rem;
  @media (max-width: ${Breakpoint.mobile}) {
    height: 20rem;
    margin: ${-modalMobilePaddingInRem}rem ${-modalMobilePaddingInRem}rem ${modalMobilePaddingInRem}rem;
  }
  @media (max-width: ${Breakpoint.small}) {
    height: 15rem;
  }
`

const toRealWidth = (namedWidth: Widths): string => {
  switch (namedWidth) {
    case 'small':
      return '58rem'
    case 'medium':
      return '80rem'
    case 'full-width':
      return '100%'
    default:
      return '58rem'
  }
}

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const Container = styled.div<ContainerProps>`
  background: ${(p) => p.background};
  width: ${(p) => toRealWidth(p.width ?? 'small')};
  padding: ${modalPaddingInRem}rem;
  border-radius: 2rem;
  outline: none;
  max-height: 100vh;
  overflow-y: auto;

  @media (max-device-width: ${Breakpoint.mobile}) {
    padding: ${modalMobilePaddingInRem}rem;
  }
`

const Icon = styled.div`
  cursor: pointer;
  margin-left: 2rem;
`
